import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import DataTable from "@/components/DataTable";
import { ErrorText, Spinner } from "@/components/ui";
import { api } from "@/lib/api";

interface DbTable {
  name: string;
  type: string;
  rows: number;
}

/** Read-only look at a SQLite file in a folder: the model changes it through its files__sql tool. */
export default function DatabaseView({ folderId, path, modified }: { folderId: string; path: string; modified: string }) {
  const base = `/api/folders/${folderId}/database`;
  const file = encodeURIComponent(path);
  const tables = useQuery({ queryKey: ["folder", folderId, "database", path, modified], queryFn: () => api.get<DbTable[]>(`${base}?path=${file}`) });
  const [chosen, setChosen] = useState<string | null>(null);
  const table = tables.data?.find((t) => t.name === chosen)?.name ?? tables.data?.[0]?.name;
  const rows = useQuery({
    queryKey: ["folder", folderId, "database", path, modified, table],
    queryFn: () => api.get<{ columns: string[]; rows: string[][]; total: number }>(`${base}/rows?path=${file}&table=${encodeURIComponent(table!)}`),
    enabled: table !== undefined,
  });

  if (tables.error || rows.error) return <ErrorText error={tables.error || rows.error} />;
  if (tables.data?.length === 0) return <p className="p-6 text-center text-sm text-zinc-500">This database has no tables yet.</p>;
  if (!rows.data || rows.isFetching) {
    return (
      <div className="p-6 text-center">
        <Spinner />
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col">
      {rows.data.total > rows.data.rows.length && (
        <p className="border-b border-zinc-200 px-3 py-1.5 text-xs text-amber-700 dark:border-zinc-800 dark:text-amber-400">
          Showing the first {rows.data.rows.length.toLocaleString()} of {rows.data.total.toLocaleString()} rows.
        </p>
      )}
      <div className="min-h-0 flex-1">
        <DataTable key={table} names={rows.data.columns} body={rows.data.rows} emptyText="This table is empty.">
          <select className="max-w-48 rounded border border-zinc-200 bg-transparent px-1 py-0.5 dark:border-zinc-700 dark:bg-zinc-950" aria-label="Table" value={table} onChange={(e) => setChosen(e.target.value)}>
            {tables.data?.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name} ({t.rows.toLocaleString()})
              </option>
            ))}
          </select>
        </DataTable>
      </div>
    </div>
  );
}
